import api, { withLoading } from '@/api/api';
import parseParams from "@/helpers/objectToQueryParams";

/**
 * @typedef {object} NivelHierarquico
 * @property {number} id - Identificador único do nível hierárquico
 * @property {string} nome - Nome do nível hierárquico
 * @property {number|null} tipo_id - ID do tipo de nível hierárquico
 * @property {object|null} tipo - Detalhe do tipo de nível hierárquico
 */

/**
 * @typedef {object} NivelHierarquicoTipo
 * @property {number} id - Identificador único do tipo de nível hierárquico
 * @property {string} nome - Nome do tipo de nível hierárquico
 * @property {string} slug - Slug do tipo de nível hierárquico
 */

/**
 * Payload de criação/atualização de um nível hierárquico.
 *
 * @typedef {object} NivelHierarquicoPayload
 * @property {string} nome - Nome do nível hierárquico
 * @property {number|null} tipo_id - ID do tipo de nível hierárquico
 */

const resource = {
  base: (empresaId) => `v1/ativacaoped/${empresaId}/nivel-hierarquicos`,
  item: (empresaId, nivelHierarquicoId) => `v1/ativacaoped/${empresaId}/nivel-hierarquicos/${nivelHierarquicoId}`
};

// Recurso derivado: os tipos ficam sob a mesma base (`/tipos`).
const resourceTipos = (empresaId) => resource.base(empresaId).concat(`/tipos`);

const LOADING_KEY = 'ativacaoped-niveis-hierarquicos';

/**
 * Lista os níveis hierárquicos disponíveis.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {object|string} [params={}] - Parâmetros de consulta (objeto) ou query string já montada (MasterDetail)
 * @returns {Promise<NivelHierarquico[]>} Lista de níveis hierárquicos
 */
export const getNiveisHierarquicos = (empresaId, params = {}) => {
  // Aceita a query string crua montada pelo MasterDetail (page/per_page/sort/filters).
  const query = typeof params === "string" ? params : parseParams(params);
  const url = resource.base(empresaId).concat(query ? `?${query}` : "");

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(res => res.data)
  );
};

/**
 * Lista os tipos de nível hierárquico disponíveis.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {object} [params={}] - Parâmetros de consulta
 * @returns {Promise<NivelHierarquicoTipo[]>} Lista de tipos de nível hierárquico
 */
export const getTiposNivelHierarquico = (empresaId, params = {}) => {
  const query = Object.keys(params).length
    ? `?${new URLSearchParams(params).toString()}`
    : "";

  return withLoading(LOADING_KEY, () =>
    api.get(`${resourceTipos(empresaId)}${query}`).then(res => res.data)
  );
};

/**
 * Lista os níveis hierárquicos simplificados para seleção (dropdown/autocomplete).
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {object|string} [params={}] - Parâmetros de consulta (objeto) ou query string já montada
 * @returns {Promise<NivelHierarquico[]>} Lista de níveis hierárquicos
 */
export const getSelecaoNiveisHierarquicos = (empresaId, params = {}) => {
  const query = typeof params === "string"
    ? params
    : new URLSearchParams(params).toString();
  const url = resource.base(empresaId).concat(`/selecao${query ? `?${query}` : ""}`);

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(res => res.data)
  );
};

/**
 * Cria um nível hierárquico.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {NivelHierarquicoPayload} data - Payload (`nome`, `tipo_id`)
 * @returns {Promise<NivelHierarquico>} Nível hierárquico criado
 */
export const postNivelHierarquico = (empresaId, data) => {
  return withLoading(LOADING_KEY, () =>
    api
      .post(resource.base(empresaId), data)
      .then(res => res.data.data || res.data)
  );
};

/**
 * Atualiza um nível hierárquico existente (backend usa POST para update).
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} nivelHierarquicoId - ID do nível hierárquico
 * @param {NivelHierarquicoPayload} data - Payload parcial (`nome`, `tipo_id`)
 * @returns {Promise<NivelHierarquico>} Nível hierárquico atualizado
 */
export const putNivelHierarquico = (empresaId, nivelHierarquicoId, data) => {
  return withLoading(LOADING_KEY, () =>
    api
      .post(resource.item(empresaId, nivelHierarquicoId), data)
      .then(res => res.data.data || res.data)
  );
};

/**
 * Exclui um nível hierárquico.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} nivelHierarquicoId - ID do nível hierárquico
 * @returns {Promise<void>}
 */
export const deleteNivelHierarquico = (empresaId, nivelHierarquicoId) => {
  return withLoading(LOADING_KEY, () =>
    api
      .delete(resource.item(empresaId, nivelHierarquicoId))
      .then(res => res.data)
  );
};

export default {
  getNiveisHierarquicos,
  getTiposNivelHierarquico,
  getSelecaoNiveisHierarquicos,
  postNivelHierarquico,
  putNivelHierarquico,
  deleteNivelHierarquico
};